'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import I18n from 'I18n';
import H2 from 'UIComponents/elements/headings/H2';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import UIButton from 'UIComponents/button/UIButton';
import { BATTLESHIP, EERIE } from 'HubStyleTokens/colors';
import { FavoritesLessonToggleIcon, FavoritesTrackToggleIcon } from '../../components/TrackCard/FavoritesToggle/FavoritesToggleIcon';
import { getTrack } from '../../selectors/trackSelectors';
import { device } from '../../constants/StyledComponentConstants';
import ShareButtonContainer from './ShareButtonContainer';
import { getLesson } from '../../selectors/lessonSelectors';
import UsageTracker from '../../utils/UsageTracker';
import { RouterSelectSingle } from '../TracksPage/RouterDropdownFilter';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import MobileNestedMenu, { CaretForward } from '../../components/Modals/FullScreenModal/MobileNestedMenu';
import useMediaQuery from '../../hooks/useMediaQuery';
import MobileFilterSingleSelect from '../TracksPage/MobileFilterSingleSelect';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import { getAvailableLanguagesByLessonId, getAvailableLanguagesByTrackId } from '../../actions/AvailableLanguagesActions';
import { availableLanuguagesByLessonId, availableLanuguagesByTrackId } from '../../selectors/availableLanguagesSelectors';
import ImmutablePropTypes from 'react-immutable-proptypes';
import RequestLanguageButton from './RequestLanguageButton';
import UIIconButton from 'UIComponents/button/UIIconButton';
import { SLINKY } from 'HubStyleTokens/colors';
import { CardMenuIcon, MenuButton } from '../../features/cardMenu';
import { parse } from 'hub-http/helpers/params';
import { HasParentPlaylist, PlaylistNavigationDropdown } from '../../features/playlistContent';
import PanelSection from '../../components/Modals/FullScreenModal/PanelSection';
const StyledUIHeader = styled.div.withConfig({
  displayName: "LearningPageHeader__StyledUIHeader",
  componentId: "sc-1tk4c8-0"
})(["position:sticky;top:0;z-index:2;background:white;border-bottom:1px solid ", ";margin:0;padding:16px 23px;@media screen and ", "{padding:16px 11px;}"], BATTLESHIP, device.tablet);
const Title = styled(H2).withConfig({
  displayName: "LearningPageHeader__Title",
  componentId: "sc-1tk4c8-1"
})(["margin:0;@media screen and ", "{font-size:16px;}min-width:90%;"], device.mobile);
const SubTitle = styled(Title).withConfig({
  displayName: "LearningPageHeader__SubTitle",
  componentId: "sc-1tk4c8-2"
})(["font-size:14px;color:", " !important;margin-left:1px;@media screen and ", "{font-size:13px;}"], SLINKY, device.mobile);
const IconContainer = styled(UIFlex).withConfig({
  displayName: "LearningPageHeader__IconContainer",
  componentId: "sc-1tk4c8-3"
})(["> *{margin-left:30px !important;}"]);
const VerticalRule = styled.span.withConfig({
  displayName: "LearningPageHeader__VerticalRule",
  componentId: "sc-1tk4c8-4"
})(["margin-right:5px;border-right:1px solid ", ";height:30px;position:relative;"], BATTLESHIP);
const LanguageContainer = styled.span.withConfig({
  displayName: "LearningPageHeader__LanguageContainer",
  componentId: "sc-1tk4c8-5"
})(["white-space:nowrap;"]);
const UIIconLanguage = styled(UIIcon).withConfig({
  displayName: "LearningPageHeader__UIIconLanguage",
  componentId: "sc-1tk4c8-6"
})(["top:5px;position:relative;"]);
const RouterSelectSingleLanguage = styled(RouterSelectSingle).withConfig({
  displayName: "LearningPageHeader__RouterSelectSingleLanguage",
  componentId: "sc-1tk4c8-7"
})(["padding-right:0 !important;"]);
const UIButtonFullWidth = styled(UIButton).withConfig({
  displayName: "LearningPageHeader__UIButtonFullWidth",
  componentId: "sc-1tk4c8-8"
})(["flex-grow:1;justify-content:space-between;display:flex;font-weight:initial;"]);
const CardMenuIconContainer = styled.div.withConfig({
  displayName: "LearningPageHeader__CardMenuIconContainer",
  componentId: "sc-1tk4c8-9"
})([".card-menu-icon{background-color:transparent;border:2px solid ", ";}"], EERIE);

const LearningPageHeader = ({
  ContentName,
  availableLanguages,
  getLessonLanguages,
  getTrackLanguages,
  trackId,
  language,
  lessonId,
  title,
  studyGuide
}) => {
  const location = useLocation();
  const {
    search,
    state: {
      backLocation
    } = {}
  } = location;
  const params = parse(search.slice(1));
  const [backLink, setBackLink] = useState('');
  const isMobile = useMediaQuery(`screen and ${device.mobile}`);
  const [mobileSubNavOpen, setMobileSubNavOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMobileSubNavOpen(false);
    UsageTracker.track('interaction', {
      action: 'learning-header-nav-closed',
      trackId,
      language,
      lessonId,
      title
    });
  }, [trackId, language, lessonId, title]);
  const trackDownloadStudyGuide = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'learning-header-study-guide-click',
      trackId,
      language,
      lessonId,
      title
    });
  }, [trackId, language, lessonId, title]);
  if (!backLink && backLocation) setBackLink(backLocation);
  useEffect(() => {
    if (trackId) {
      getTrackLanguages(trackId);
    } else {
      getLessonLanguages(lessonId);
    }
  }, [getLessonLanguages, lessonId, getTrackLanguages, trackId]);
  const WithIdsRequestLanguageButton = useCallback(props => /*#__PURE__*/_jsx(RequestLanguageButton, Object.assign({
    lessonId: lessonId,
    trackId: trackId
  }, props)), [trackId, lessonId]);
  return /*#__PURE__*/_jsxs(StyledUIHeader, {
    children: [/*#__PURE__*/_jsxs(UIFlex, {
      align: "center",
      direction: "row",
      wrap: "nowrap",
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        grow: true,
        children: [/*#__PURE__*/_jsx(HasParentPlaylist, {
          children: /*#__PURE__*/_jsx(PlaylistNavigationDropdown, {
            lessonId: lessonId,
            playlistId: params.playlistId,
            trackId: trackId
          })
        }), /*#__PURE__*/_jsx(Title, {
          children: title || /*#__PURE__*/_jsx(SkeletonText, {})
        }), /*#__PURE__*/_jsx(SubTitle, {
          children: ContentName
        })]
      }), isMobile ? /*#__PURE__*/_jsx(CardMenuIconContainer, {
        style: {
          marginRight: 4
        },
        children: /*#__PURE__*/_jsx(CardMenuIcon, {
          language: language,
          lessonId: Number(lessonId),
          placement: "bottom left",
          renderExtraOptions: setIsOpen => /*#__PURE__*/_jsxs("div", {
            nestedOptions: true,
            children: [/*#__PURE__*/_jsx(PanelSection, {
              showBorders: false,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.lessonInformation.mobileMenuContentHeader"
              })
            }), !!studyGuide && /*#__PURE__*/_jsx(MenuButton, {
              "data-test-id": "track-studyguide-button",
              external: true,
              href: studyGuide,
              iconProps: {
                color: EERIE
              },
              onClick: () => setIsOpen(false),
              size: "small",
              target: "_blank",
              use: "unstyled",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.resourcesList.downloadStudyGuide"
              })
            }), /*#__PURE__*/_jsxs(MenuButton, {
              subpage: /*#__PURE__*/_jsx(ShareButtonContainer, {
                mobileButtons: true,
                onClick: () => setIsOpen(false)
              }),
              use: "unstyled",
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.shareThis.button"
              }), /*#__PURE__*/_jsx("span", {
                children: /*#__PURE__*/_jsx(CaretForward, {
                  color: EERIE
                })
              })]
            }), /*#__PURE__*/_jsxs(MenuButton, {
              subpage: /*#__PURE__*/_jsx(MobileFilterSingleSelect, {
                allowList: availableLanguages,
                buttonStyle: {
                  padding: '12px 20px'
                },
                defaultItem: language.toUpperCase(),
                filterType: FILTER_TYPES.LANGUAGE,
                lessonId: lessonId,
                onSelect: () => setIsOpen(false),
                trackId: trackId
              }),
              use: "unstyled",
              children: [I18n.text('learningCenter.topFilters.language.filterTitle'), ": ", LanguageNames[language.toLowerCase()], /*#__PURE__*/_jsx("span", {
                children: /*#__PURE__*/_jsx(CaretForward, {
                  color: EERIE
                })
              })]
            })]
          }),
          trackId: Number(trackId)
        })
      }) : /*#__PURE__*/_jsxs(IconContainer, {
        align: "center",
        direction: "row",
        justify: "end",
        wrap: "nowrap",
        children: [/*#__PURE__*/_jsxs(LanguageContainer, {
          children: [/*#__PURE__*/_jsx(UIIconLanguage, {
            color: EERIE,
            name: "language",
            size: "xs",
            style: {
              marginTop: '10px'
            }
          }), /*#__PURE__*/_jsx(RouterSelectSingleLanguage, {
            allowList: availableLanguages,
            defaultItem: language.toUpperCase(),
            filterType: FILTER_TYPES.LANGUAGE,
            menuStyle: {
              maxHeight: '270px'
            },
            menuWidth: "auto",
            optionComponent: WithIdsRequestLanguageButton,
            trackingKey: "language-switcher"
          })]
        }), /*#__PURE__*/_jsx(VerticalRule, {}), !!trackId && studyGuide && /*#__PURE__*/_jsx(UIIconButton, {
          "data-test-id": "track-studyguide-button",
          href: studyGuide,
          onClick: trackDownloadStudyGuide,
          placement: "bottom",
          target: "_blank",
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.resourcesList.downloadStudyGuide"
          }),
          use: "link",
          children: /*#__PURE__*/_jsx(UIIcon, {
            color: EERIE,
            name: "download",
            size: "xs",
            style: {
              marginRight: 0,
              marginTop: '3px'
            }
          })
        }), /*#__PURE__*/_jsx(CardMenuIconContainer, {
          children: /*#__PURE__*/_jsx(CardMenuIcon, {
            language: language,
            lessonId: Number(lessonId),
            placement: "bottom left",
            trackId: Number(trackId)
          })
        }), /*#__PURE__*/_jsx(ShareButtonContainer, {})]
      })]
    }), mobileSubNavOpen && /*#__PURE__*/_jsxs(MobileNestedMenu, {
      indented: true,
      open: mobileSubNavOpen,
      setIsOpen: setMobileSubNavOpen,
      useBackText: true,
      useSidePanel: true,
      children: [lessonId ? /*#__PURE__*/_jsx(FavoritesLessonToggleIcon, {
        lessonId: lessonId,
        noShadow: true,
        use: "on-bright",
        useFullButton: true
      }) : /*#__PURE__*/_jsx(FavoritesTrackToggleIcon, {
        noShadow: true,
        trackId: trackId,
        use: "on-bright",
        useFullButton: true
      }), !!studyGuide && /*#__PURE__*/_jsx(UIButtonFullWidth, {
        "data-test-id": "track-studyguide-button",
        external: true,
        href: studyGuide,
        iconProps: {
          color: EERIE
        },
        size: "small",
        target: "_blank",
        use: "unstyled",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.resourcesList.downloadStudyGuide"
        })
      }), /*#__PURE__*/_jsxs(UIButtonFullWidth, {
        subpage: /*#__PURE__*/_jsx(ShareButtonContainer, {
          mobileButtons: true
        }),
        use: "unstyled",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.shareThis.button"
        }), /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(CaretForward, {
            color: EERIE
          })
        })]
      }), /*#__PURE__*/_jsxs(UIButtonFullWidth, {
        subpage: /*#__PURE__*/_jsx(MobileFilterSingleSelect, {
          allowList: availableLanguages,
          defaultItem: language.toUpperCase(),
          filterType: FILTER_TYPES.LANGUAGE,
          lessonId: lessonId,
          onSelect: closeMenu,
          trackId: trackId
        }),
        use: "unstyled",
        children: [I18n.text('learningCenter.topFilters.language.filterTitle'), ":", ' ', LanguageNames[language.toLowerCase()], /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(CaretForward, {
            color: EERIE
          })
        })]
      })]
    })]
  });
};

LearningPageHeader.propTypes = {
  ContentName: PropTypes.node,
  availableLanguages: ImmutablePropTypes.listOf(PropTypes.string),
  defaultBackLink: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string
  }),
  getLessonLanguages: PropTypes.func.isRequired,
  getTrackLanguages: PropTypes.func.isRequired,
  language: PropTypes.string,
  lessonId: PropTypes.string,
  studyGuide: PropTypes.string,
  title: PropTypes.string,
  trackId: PropTypes.string
};

const mapStateToProps = () => {
  return (state, {
    trackId,
    lessonId
  }) => {
    let availableLanguages;
    let track;
    let title;
    let studyGuide;

    if (trackId) {
      availableLanguages = availableLanuguagesByTrackId(state, {
        trackId
      });
      track = getTrack(state, {
        trackId
      });
      title = track && track.get('name');
      studyGuide = track && track.get('studyGuide');
    } else {
      availableLanguages = availableLanuguagesByLessonId(state, {
        lessonId
      });
      const lesson = getLesson(state, {
        lessonId
      });
      title = lesson.get('name');
    }

    return {
      availableLanguages,
      title,
      studyGuide
    };
  };
};

const mapDispatchToProps = {
  getLessonLanguages: getAvailableLanguagesByLessonId,
  getTrackLanguages: getAvailableLanguagesByTrackId
};
export default connect(mapStateToProps, mapDispatchToProps)(LearningPageHeader);